/**
 * 会话管理页面
 *
 * 管理员可以查看和筛选用户会话
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
    Table,
    Tag,
    Input,
    Select,
    DatePicker,
    Button,
    Space,
    Card,
} from 'antd';
import { SearchOutlined, BellOutlined } from '@ant-design/icons';
import { conversationApi } from '@/services/api';
import { useAuthStore } from '@/stores/authStore';
import dayjs, { formatDateTimeShort } from '@/utils/date';
import type {
    AdminConversationItem,
    AdminConversationListResponse,
    AdminConversationListParams,
} from '@/services/types';
import ConversationDetailDrawer from '@/components/admin/ConversationDetailDrawer';

const { RangePicker } = DatePicker;

// 状态标签颜色映射
const STATUS_COLORS: Record<string, { bg: string; text: string; label: string }> = {
    active: { bg: '#DCFCE7', text: '#16A34A', label: '活跃' },
    closed: { bg: '#F3F4F6', text: '#6B7280', label: '已关闭' },
    archived: { bg: '#FEF3C7', text: '#D97706', label: '已归档' },
};

// 统一状态标签组件
const StatusTag: React.FC<{ status: string }> = ({ status }) => {
    const config = STATUS_COLORS[status] || STATUS_COLORS.active;
    return (
        <Tag
            style={{
                backgroundColor: config.bg,
                color: config.text,
                border: 'none',
                borderRadius: 12,
                padding: '2px 12px',
            }}
        >
            {config.label}
        </Tag>
    );
};


const ConversationsPage: React.FC = () => {
    // 状态定义
    const { user } = useAuthStore();
    const [loading, setLoading] = useState(false);
    const [data, setData] = useState<AdminConversationListResponse | null>(null);
    const [params, setParams] = useState<AdminConversationListParams>({
        page: 1,
        page_size: 20,
    });
    const [selectedSessionId, setSelectedSessionId] = useState<string | null>(null);
    const [drawerOpen, setDrawerOpen] = useState(false);

    // 搜索输入
    const [searchValue, setSearchValue] = useState('');
    const [statusFilter, setStatusFilter] = useState<string | undefined>();
    const [dateRange, setDateRange] = useState<[dayjs.Dayjs | null, dayjs.Dayjs | null] | null>(null);
    const [destroyingId, setDestroyingId] = useState<string | null>(null);

    // 加载数据
    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await conversationApi.list(params);
            setData(res);
        } catch (error) {
            console.error('加载会话列表失败:', error);
        } finally {
            setLoading(false);
        }
    }, [params]);

    // 初始加载
    useEffect(() => {
        loadData();
    }, [loadData]);

    // 处理搜索
    const handleSearch = () => {
        setParams({
            ...params,
            page: 1,
            username: searchValue || undefined,
            status: statusFilter as AdminConversationListParams['status'] || ('active' as const), // 默认或指定状态
            start_date: dateRange?.[0]?.toISOString(),
            end_date: dateRange?.[1]?.toISOString(),
        });
    };

    // 处理归档会话
    const handleArchive = async (record: AdminConversationItem) => {
        setDestroyingId(record.id);
        try {
            await conversationApi.archive(record.session_id);
            // 刷新列表
            loadData();
        } catch (error) {
            console.error('归档会话失败:', error);
        } finally {
            setDestroyingId(null);
        }
    };

    // 处理表格行点击
    const handleRowClick = (record: AdminConversationItem) => {
        setSelectedSessionId(record.session_id);
        setDrawerOpen(true);
    };

    // 表格列定义
    const columns = [
        {
            title: '会话ID',
            dataIndex: 'session_id',
            key: 'session_id',
            width: 150,
            render: (text: string) => (
                <a style={{ fontFamily: 'monospace', fontSize: 13, color: '#1677ff' }}>{text}</a>
            ),
        },
        {
            title: '用户',
            dataIndex: 'username',
            key: 'username',
            width: 100,
            render: (text: string | null) =>
                text || <span style={{ color: '#A1A1AA' }}>匿名用户</span>,
        },
        {
            title: '状态',
            dataIndex: 'status',
            key: 'status',
            width: 100,
            render: (status: string) => <StatusTag status={status} />,
        },
        {
            title: '消息数',
            dataIndex: 'message_count',
            key: 'message_count',
            width: 100,
        },
        {
            title: '最后消息',
            dataIndex: 'last_message',
            key: 'last_message',
            ellipsis: true,
            render: (text: string | null) => (
                <span style={{ color: '#71717A' }}>{text || '-'}</span>
            ),
        },
        {
            title: '创建时间',
            dataIndex: 'started_at',
            key: 'started_at',
            width: 150,
            render: (text: string) => (
                <span style={{ color: '#71717A', fontSize: 13 }}>
                    {formatDateTimeShort(text)}
                </span>
            ),
        },
        {
            title: '操作',
            key: 'action',
            width: 150,
            fixed: 'right' as const,
            render: (_: any, record: AdminConversationItem) => (
                <Space size="small">
                    <Button
                        type="link"
                        size="small"
                        onClick={() => handleRowClick(record)}
                    >
                        查看详情
                    </Button>
                    {record.status === 'closed' && (
                        <Button
                            type="link"
                            size="small"
                            loading={destroyingId === record.id}
                            style={{ color: '#4b5563', padding: 0 }}
                            onClick={() => {
                                if (confirm(`确定要归档此会话吗？归档后将不再显示。`)) {
                                    handleArchive(record);
                                }
                            }}
                        >
                            归档
                        </Button>
                    )}
                </Space>
            ),
        },
    ];

    return (
        <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            {/* 顶部栏 */}
            <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                padding: '0 32px', height: 64, background: '#fff',
                borderBottom: '1px solid #e4e4e7', flexShrink: 0,
            }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 600, color: '#18181b' }}>会话管理</h2>
                <Space>
                    <BellOutlined style={{ fontSize: 18, color: '#71717a', cursor: 'pointer' }} />
                    <div style={{
                        width: 32, height: 32, borderRadius: '50%', background: '#3b82f6',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 600, fontSize: 14,
                    }}>
                        {user?.username?.[0]?.toUpperCase() || 'A'}
                    </div>
                </Space>
            </div>

            <div style={{ padding: 24, flex: 1, overflow: 'auto' }}>
                {/* 筛选栏 */}
                <Card style={{ marginBottom: 16, borderRadius: 12 }}>
                    <Space size={12} wrap>
                        <Input.Search
                            placeholder="搜索用户..."
                            style={{ width: 240 }}
                            value={searchValue}
                            onChange={(e) => setSearchValue(e.target.value)}
                            onSearch={handleSearch}
                            prefix={<SearchOutlined style={{ color: '#A1A1AA' }} />}
                            allowClear
                        />
                        <Select
                            style={{ width: 120 }}
                            placeholder="全部状态"
                            allowClear
                            value={statusFilter}
                            onChange={(value) => setStatusFilter(value)}
                            options={[
                                { value: 'active', label: '活跃' },
                                { value: 'closed', label: '已关闭' },
                                { value: 'archived', label: '已归档' },
                            ]}
                        />
                        <RangePicker
                            value={dateRange}
                            onChange={(dates) =>
                                setDateRange(dates as [dayjs.Dayjs | null, dayjs.Dayjs | null] | null)
                            }
                            showTime
                        />
                        <Button type="primary" onClick={handleSearch}>
                            筛选
                        </Button>
                        <Button onClick={() => {
                            setSearchValue('');
                            setStatusFilter(undefined);
                            setDateRange(null);
                            setParams({ page: 1, page_size: 20 });
                        }}>
                            重置
                        </Button>
                    </Space>
                </Card>

                {/* 表格 */}
                <Card style={{ borderRadius: 12 }}>
                    <Table
                        loading={loading}
                        columns={columns}
                        dataSource={data?.items}
                        rowKey="id"
                        pagination={{
                            current: data?.page || 1,
                            pageSize: data?.page_size || 20,
                            total: data?.total || 0,
                            showSizeChanger: true,
                            showQuickJumper: true,
                            hideOnSinglePage: false,
                            showTotal: (total) => `共 ${total} 条`,
                            onChange: (page, pageSize) =>
                                setParams({ ...params, page, page_size: pageSize }),
                        }}
                        onRow={(record) => ({
                            onClick: () => handleRowClick(record),
                            style: { cursor: 'pointer' },
                        })}
                        rowHoverable
                        scroll={{ x: 800 }}
                    />
                </Card>

                {/* 详情抽屉 */}
                <ConversationDetailDrawer
                    sessionId={selectedSessionId}
                    open={drawerOpen}
                    onClose={() => {
                        setDrawerOpen(false);
                        setSelectedSessionId(null);
                    }}
                />
            </div>
        </div>
    );
};

export default ConversationsPage;